import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LoyaltyTagAPIPromiseClient } from "gen/witbooking/loyalty/tag/v1/loyalty_tag_api_grpc_web_pb";
import { useCallback, useMemo } from "react";
import { useGrpcClient } from "../../../../lib/grpc";
import { LoyaltyTag } from "../../domain/LoyaltyTag";
import { LoyaltyTagGateway } from "../gateways/LoyaltyTagGateway";
import { sectionKeys } from "./sections.hook";

const STALE_TIME = 60 * 1000;

type UseTagsOptions = {
  loadTags?: boolean;
  globals?: boolean;
};

type UseTagsWithoutTags = {
  getTags: () => Promise<LoyaltyTag[]>;
  saveTag: (tag: LoyaltyTag) => Promise<void>;
  updateTag: (tag: LoyaltyTag) => Promise<void>;
  deleteTag: (id: string) => Promise<void>;
};

type UseTagsWithTags = UseTagsWithoutTags & {
  tags: LoyaltyTag[] | undefined;
  isLoading: boolean;
};

type UseTags = UseTagsWithoutTags | UseTagsWithTags;

export const tagKeys = {
  all: ["tags"] as const,
  getAll: (brandId: string, globals: boolean = false) =>
    [...tagKeys.all, brandId, globals] as const,
  get: (brandId: string, id: string) => [...tagKeys.getAll(brandId), "detail", id] as const,
};

export const useTag = (brandId: string, id?: string) => {
  const grpcClient = useGrpcClient(LoyaltyTagAPIPromiseClient);

  const loyaltyTagGateway = useMemo(() => new LoyaltyTagGateway(grpcClient), [grpcClient]);

  const { data: tag, isLoading } = useQuery({
    queryKey: tagKeys.get(brandId, id ?? ""),
    queryFn: () => loyaltyTagGateway.get(brandId, id ?? ""),
    staleTime: STALE_TIME,
    enabled: !!id,
  });

  return { tag, isLoading };
};

export function useTags(brandId: string): UseTagsWithoutTags;
export function useTags(brandId: string, options: UseTagsOptions): UseTagsWithTags;
export function useTags(brandId: string, options?: UseTagsOptions): UseTags {
  const queryClient = useQueryClient();
  const grpcClient = useGrpcClient(LoyaltyTagAPIPromiseClient);

  const loyaltyTagGateway = useMemo(() => new LoyaltyTagGateway(grpcClient), [grpcClient]);

  const { data: tags, isLoading } = useQuery({
    queryKey: tagKeys.getAll(brandId, options?.globals),
    queryFn: () => loyaltyTagGateway.getAll(brandId, options?.globals),
    staleTime: STALE_TIME,
    enabled: (!!brandId && options?.loadTags) ?? false,
  });

  const invalidateTags = useCallback(
    (queryKey: ReadonlyArray<string | boolean>) => queryClient.invalidateQueries({ queryKey }),
    [queryClient]
  );

  const getTags = useCallback(
    (globals: boolean = false) => {
      return queryClient.fetchQuery({
        queryKey: tagKeys.getAll(brandId, globals),
        queryFn: () => loyaltyTagGateway.getAll(brandId, globals),
        staleTime: STALE_TIME,
      });
    },
    [brandId, loyaltyTagGateway, queryClient]
  );

  const saveTag = useCallback(
    async (tag: LoyaltyTag) => {
      await loyaltyTagGateway.save(brandId, tag);
      await invalidateTags(tagKeys.getAll(brandId));
    },
    [brandId, invalidateTags, loyaltyTagGateway]
  );

  const updateTag = useCallback(
    async (tag: LoyaltyTag) => {
      await loyaltyTagGateway.update(brandId, tag);
      await invalidateTags(tagKeys.getAll(brandId));
    },
    [brandId, invalidateTags, loyaltyTagGateway]
  );

  const deleteTag = useCallback(
    async (id: string) => {
      await loyaltyTagGateway.delete(brandId, id);
      await invalidateTags(tagKeys.getAll(brandId));
      await queryClient.invalidateQueries({ queryKey: sectionKeys.all });
    },
    [brandId, invalidateTags, loyaltyTagGateway, queryClient]
  );

  return { tags, isLoading, getTags, saveTag, updateTag, deleteTag };
}
